import { Component, inject, signal, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { GateCatalogService } from "../../services/api/gate-catalog/gate-catalog.service";
import { GATE_CATALOG_ITEM_TYPES } from "../../enums/gateCatalogItemTypes.enum";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { SvgIconComponent } from "../../shared/components/svg-icon/svg-icon.component";
import { DecimalPipe, NgClass } from "@angular/common";
import { TableModule } from "@openng/optimus-ui/table";
import { ManufacturerComponent } from "./components/forms/manufacturer/manufacturer.component";
import { DialogService, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { ConfirmationService } from "@openng/optimus-ui/api";
import { MakeComponent } from "./components/forms/make/make.component";
import { ArticleComponent } from "./components/forms/article/article.component";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { cloneDeep } from "lodash";
import { HasPermissionDirective } from "../../shared/directives/has-permission/has-permission.directive";
import { PermissionService } from "../../services/api/permission/permission.service";
import { createTimeoutSignal } from "../../shared/utils/timeout.utils";
import { TimeoutComponent } from "../../shared/components/timeout/timeout.component";
import { SimplebarAngularModule } from "simplebar-angular";
import { Article } from "../../models/api/gateComponents/article";
import { GateCatalogItem } from "../../models/api/gateComponents/gateCatalogItem";
import { Make } from "../../models/api/gateComponents/make";
import { Manufacturer } from "../../models/api/gateComponents/manufacturer";
import { Column } from "../../models/ui/tableColumn";
import { first } from "rxjs";
import { ButtonComponent } from "../../shared/components/button/button.component";
import { GateCatalogImportDialogComponent } from "./components/gate-catalog-import-dialog/gate-catalog-import-dialog.component";

@Component({
  standalone: true,
  selector: "app-gate-components",
  imports: [
    TranslateModule,
    SvgIconComponent,
    NgClass,
    TableModule,
    DecimalPipe,
    ProgressSpinnerModule,
    HasPermissionDirective,
    TimeoutComponent,
    SimplebarAngularModule,
    ButtonComponent,
  ],
  templateUrl: "./gate-components.component.html",
  styleUrl: "./gate-components.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [],
})
export class GateComponentsComponent implements OnInit {
  public manufacturers!: Manufacturer[];
  public makes!: Make[] | undefined;
  public articles: Article[] | undefined;

  public selectedManufacturer!: Manufacturer;
  public selectedMake!: Make;

  public loadingManufacturers = signal(false);
  public timeoutManufacturers = createTimeoutSignal(this.loadingManufacturers);

  public loadingMakes = signal(false);
  public timeoutMakes = createTimeoutSignal(this.loadingMakes);

  public loadingArticles = signal(false);
  public timeoutArticles = createTimeoutSignal(this.loadingArticles);

  private gateCatalog = inject(GateCatalogService);
  private translate = inject(TranslateService);
  private dialog = inject(DialogService);
  private confirmation = inject(ConfirmationService);
  public permissions = inject(PermissionService);
  private ref: DynamicDialogRef | null = null;

  public gateCatalogItemTypes = GATE_CATALOG_ITEM_TYPES;

  public articleTableCols: Column[] = [
    { field: "label", header: "gate_components.article_table.gate_component" },
    {
      field: "loadChanges",
      header: "gate_components.article_table.load_changes",
    },
    {
      field: "operatingTime",
      header: "gate_components.article_table.operating_time",
    },
    {
      field: "maxLoadChanges",
      header: "gate_components.article_table.max_load_changes",
    },
    {
      field: "maxOperatingTime",
      header: "gate_components.article_table.max_operating_time",
    },
  ];

  ngOnInit() {
    this.getManufacturers();
  }

  /**
   * Retrieves all available gate component manufacturers from the gate catalog.
   * Sets the initial manufacturer selection to the first available manufacturer.
   * @param refresh If true, the cache will be ignored and the list will be reloaded.
   */
  private getManufacturers(refresh = false) {
    this.loadingManufacturers.set(true);
    this.gateCatalog
      .getCatalogItems(GATE_CATALOG_ITEM_TYPES.MANUFACTURER, undefined, refresh)
      .pipe(first())
      .subscribe({
        next: (items) => {
          if (items) {
            this.manufacturers = items.map((manufacturer) => {
              if (manufacturer && manufacturer?.id === this.selectedManufacturer?.id) {
                manufacturer.selected = true;
              }
              return manufacturer;
            });
          }
          this.loadingManufacturers.set(false);
        },
      });
  }

  /**
   * Un-selects all other manufacturers except the given one.
   * @param manufacturer - The manufacturer to select.
   */
  private unselectOtherManufacturers(manufacturer: Manufacturer) {
    this.manufacturers.forEach((m) => {
      if (m.id !== manufacturer.id) {
        m.selected = false;
      }
    });
  }

  /**
   * Selects the given manufacturer and un-selects all other manufacturers.
   * Retrieves all available makes for the selected manufacturer.
   * If the selected manufacturer has an id, it will also clear the articles list.
   * @param manufacturer The manufacturer to select.
   */
  public selectManufacturer(manufacturer: Manufacturer) {
    manufacturer.selected = true;
    this.selectedManufacturer = manufacturer;
    this.unselectOtherManufacturers(manufacturer);
    if (manufacturer.id) {
      if (this.articles) {
        this.articles = undefined;
      }
      this.getMakes(manufacturer.id);
    }
  }

  /**
   * Retrieves all available makes for the given manufacturer id.
   * Sets the initial make selection to the first available make.
   * @param manufacturerId The id of the manufacturer to fetch makes for.
   * @param refresh If true, the cache will be ignored and the list will be reloaded.
   */
  private getMakes(manufacturerId: string, refresh = false) {
    this.loadingMakes.set(true);
    this.gateCatalog
      .getCatalogItems(GATE_CATALOG_ITEM_TYPES.MAKE, manufacturerId, refresh)
      .pipe(first())
      .subscribe({
        next: (items) => {
          if (items) {
            this.makes = items.map((make) => {
              if (make.id === this.selectedMake?.id) make.selected = true;
              return make;
            });

            this.loadingMakes.set(false);
          }
        },
      });
  }

  onOpenImport() {
    this.ref = this.dialog.open(GateCatalogImportDialogComponent, {
      header: this.translate.instant("gate_components.import.title"),
      width: "50%",
      closable: true,
    });

    this.ref?.onClose.pipe(first()).subscribe({
      next: (type) => {
        switch (type) {
          case GATE_CATALOG_ITEM_TYPES.MANUFACTURER:
            this.getManufacturers(true);
            break;
          case GATE_CATALOG_ITEM_TYPES.MAKE:
            if (this.selectedManufacturer?.id) {
              this.getMakes(this.selectedManufacturer.id, true);
            }
            break;
          case GATE_CATALOG_ITEM_TYPES.ARTICLE:
            if (this.selectedMake?.id) {
              this.getArticles(this.selectedMake.id, true);
            }
            break;
          default:
            break;
        }
      },
    });
  }

  /**
   * Opens a dialog for adding a new gate catalog item of the given type.
   * The user needs to have the create permission to use this method.
   * @param gateCatalogType The type of the gate catalog item to add.
   */
  public onAddClick(gateCatalogType: string) {
    let gateCatalogItem: GateCatalogItem | null = null;
    switch (gateCatalogType) {
      case GATE_CATALOG_ITEM_TYPES.MANUFACTURER:
        gateCatalogItem = this.gateCatalog.getEmptyGateCatalogItem(gateCatalogType);
        break;
      case GATE_CATALOG_ITEM_TYPES.MAKE:
        gateCatalogItem = this.gateCatalog.getEmptyGateCatalogItem(gateCatalogType, this.selectedManufacturer.id);
        break;
      case GATE_CATALOG_ITEM_TYPES.ARTICLE:
        gateCatalogItem = this.gateCatalog.getEmptyGateCatalogItem(gateCatalogType, this.selectedMake.id);
        break;
      default:
        gateCatalogItem = this.gateCatalog.getEmptyGateCatalogItem(gateCatalogType);
        break;
    }

    this.openGateCatalogForm(gateCatalogType, "add", gateCatalogItem);
  }

  /**
   * Opens a dialog for editing a gate catalog item.
   * The dialog type is determined by the provided gate catalog type.
   * The user needs to have the edit permission to use this method.
   * @param gateCatalogType The type of the gate catalog item to edit.
   * @param gateCatalogItem The gate catalog item to edit.
   * @param event The click event that triggered this action.
   */
  public onEditClick(gateCatalogType: string, gateCatalogItem: GateCatalogItem, event: Event) {
    event.stopPropagation();
    const gateCatalogItemCopy = cloneDeep(gateCatalogItem);
    this.openGateCatalogForm(gateCatalogType, "edit", gateCatalogItemCopy);
  }

  /**
   * Opens a confirmation dialog for deleting a gate catalog item.
   * The user needs to have the delete permission to use this method.
   * @param gateCatalogType The type of the gate catalog item to delete.
   * @param gateCatalogItem The gate catalog item to delete.
   * @param event The click event that triggered this action.
   */
  public onDeleteClick(gateCatalogType: string, gateCatalogItem: GateCatalogItem, event: Event) {
    event.stopPropagation();
    this.confirmation.confirm({
      target: event.currentTarget as EventTarget,
      message: this.translate.instant("gate_components." + this.getHeaderTitle(gateCatalogType, "delete")),
      key: "global",
      accept: () => {
        this.setLoading(gateCatalogItem?.gateCatalogItemType, true);
        this.gateCatalog
          .deleteGateCatalogItem(gateCatalogItem)
          .pipe(first())
          .subscribe({
            next: () => {
              switch (gateCatalogType) {
                case GATE_CATALOG_ITEM_TYPES.MANUFACTURER:
                  if (this.selectedManufacturer?.id === gateCatalogItem?.id) {
                    this.makes = undefined;
                    this.articles = undefined;
                  }
                  break;
                case GATE_CATALOG_ITEM_TYPES.MAKE:
                  if (this.selectedMake?.id === gateCatalogItem?.id) {
                    this.articles = undefined;
                  }
              }
              this.refresh(gateCatalogItem?.gateCatalogItemType);
            },
          });
      },
    });
  }

  /**
   * Set the loading state of the given gate catalog type.
   * @param gateCatalogType The type of the gate catalog (e.g. MANUFACTURER, MAKE, ARTICLE).
   * @param loading The loading state.
   */
  private setLoading(gateCatalogType: string | undefined, loading: boolean) {
    switch (gateCatalogType) {
      case GATE_CATALOG_ITEM_TYPES.MANUFACTURER:
        this.loadingManufacturers.set(loading);
        break;
      case GATE_CATALOG_ITEM_TYPES.MAKE:
        this.loadingMakes.set(loading);
        break;
      case GATE_CATALOG_ITEM_TYPES.ARTICLE:
        this.loadingArticles.set(loading);
        break;
      default:
        break;
    }
  }

  /**
   * Reloads the gate catalog of the given type.
   * @param gateCatalogType The type of the gate catalog to reload (e.g. MANUFACTURER, MAKE, ARTICLE).
   */
  private refresh(gateCatalogType: string | undefined) {
    switch (gateCatalogType) {
      case GATE_CATALOG_ITEM_TYPES.MANUFACTURER:
        this.getManufacturers(true);
        break;
      case GATE_CATALOG_ITEM_TYPES.MAKE:
        if (this.selectedManufacturer?.id) {
          this.getMakes(this.selectedManufacturer.id, true);
        }
        break;
      case GATE_CATALOG_ITEM_TYPES.ARTICLE:
        if (this.selectedMake?.id) {
          this.getArticles(this.selectedMake.id, true);
        }
        break;
      default:
        break;
    }
  }

  /**
   * Opens a dialog for editing or adding a gate catalog item.
   * The dialog type is determined by the provided gate catalog type.
   * The user needs to have the edit or add permission to use this method.
   * @param gateCatalogType The type of the gate catalog item to edit or add (e.g. MANUFACTURER, MAKE, ARTICLE).
   * @param actionType The type of action to perform (e.g. add, edit).
   * @param gateCatalogItem The gate catalog item to edit or add.
   */
  private openGateCatalogForm(gateCatalogType: string, actionType: string, gateCatalogItem: GateCatalogItem | null) {
    if (gateCatalogItem) {
      this.ref = this.dialog.open<ManufacturerComponent | MakeComponent | ArticleComponent>(
        this.getGateCatalogForm(gateCatalogType),
        {
          data: {
            actionType: actionType,
            gateCatalogItem: gateCatalogItem,
          },
          header: this.translate.instant("gate_components." + this.getHeaderTitle(gateCatalogType, actionType)),
          modal: true,
          width: "40vw",
          contentStyle: { overflow: "auto" },
          breakpoints: {
            "960px": "75vw",
            "640px": "90vw",
          },
          closable: true,
        }
      );
      this.ref?.onClose.subscribe((gateCatalogItem: GateCatalogItem) => {
        if (gateCatalogItem) {
          this.refresh(gateCatalogItem?.gateCatalogItemType);
        }
      });
    }
  }

  /**
   * Returns the translation key for the header title of the gate catalog form based on the gate catalog type and action type.
   * @param gateCatalogType The type of the gate catalog item (e.g. MANUFACTURER, MAKE, ARTICLE).
   * @param actionType The type of action to perform (e.g. add, edit, delete).
   * @returns The translation key for the header title.
   */
  private getHeaderTitle(gateCatalogType: string, actionType: string): string {
    switch (gateCatalogType) {
      case GATE_CATALOG_ITEM_TYPES.MANUFACTURER:
        return actionType + "_manufacturer";

      case GATE_CATALOG_ITEM_TYPES.MAKE:
        return actionType + "_make";

      case GATE_CATALOG_ITEM_TYPES.ARTICLE:
        return actionType + "_article";

      default:
        return "";
    }
  }

  /**
   * Returns the Angular component class for the specified gate catalog type.
   * Defaults to ManufacturerComponent if the type is unrecognized.
   * @param gateCatalogType - The type of the gate catalog (e.g., MANUFACTURER, MAKE, ARTICLE).
   * @returns The Angular component class for the specified type.
   */
  private getGateCatalogForm(gateCatalogType: string) {
    switch (gateCatalogType) {
      case GATE_CATALOG_ITEM_TYPES.MANUFACTURER:
        return ManufacturerComponent;
      case GATE_CATALOG_ITEM_TYPES.MAKE:
        return MakeComponent;
      case GATE_CATALOG_ITEM_TYPES.ARTICLE:
        return ArticleComponent;
      default:
        return ManufacturerComponent;
    }
  }

  /**
   * Un-selects all makes except the given one.
   * @param make - The make to keep selected.
   */
  private unselectOtherMakes(make: Make) {
    this.makes?.forEach((m) => {
      if (m.id !== make.id) {
        m.selected = false;
      }
    });
  }

  /**
   * Selects the given make and un-selects all other makes.
   * If the make has an id, it will also fetch the articles for the make.
   * @param make The make to select.
   */
  public selectMake(make: Make) {
    make.selected = true;
    this.selectedMake = make;
    this.unselectOtherMakes(make);
    if (make.id) {
      this.getArticles(make.id);
    }
  }

  /**
   * Retrieves all articles for the specified make id.
   * Sets the loading state to true while fetching the data.
   * Once the articles are fetched, updates the articles list and sets the loading state to false.
   * @param makeId - The id of the make to fetch articles for.
   * @param refresh - If true, ignores the cache and reloads the list.
   */
  private getArticles(makeId: string, refresh = false) {
    this.loadingArticles.set(true);
    this.gateCatalog
      .getCatalogItems(GATE_CATALOG_ITEM_TYPES.ARTICLE, makeId, refresh)
      .pipe(first())
      .subscribe((items) => {
        if (items) {
          this.articles = items;
          this.loadingArticles.set(false);
        }
      });
  }
}
